import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Podcast, Music, Youtube, Instagram, Twitter, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRES = [
  { id: 'Business & Entrepreneurship', name: 'Business & Entrepreneurship' },
  { id: 'Culture & Society', name: 'Culture & Society' },
  { id: 'Faith & Spirituality', name: 'Faith & Spirituality' },
  { id: 'Entertainment', name: 'Entertainment' },
  { id: 'Education', name: 'Education' },
  { id: 'Health & Wellness', name: 'Health & Wellness' },
  { id: 'News & Politics', name: 'News & Politics' },
  { id: 'Sports', name: 'Sports' },
  { id: 'Technology', name: 'Technology' },
];

const AddPodcastPage = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    host_name: '',
    genre: '',
    cover_image_url: '',
    spotify_url: '',
    apple_podcasts_url: '',
    youtube_url: '',
    soundcloud_url: '',
    website_url: '',
    instagram_url: '',
    twitter_url: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Please sign in to add a podcast');
      navigate('/login');
      return;
    }

    if (!formData.title || !formData.description || !formData.host_name || !formData.genre) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Require at least one platform link
    if (!formData.spotify_url && !formData.apple_podcasts_url && !formData.youtube_url && !formData.soundcloud_url) {
      toast.error('Please add at least one platform link (Spotify, Apple, YouTube, or SoundCloud)');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/podcasts`, formData, {
        headers,
        withCredentials: true
      });
      toast.success('Podcast added successfully!');
      navigate('/podcasts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add podcast');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-white" data-testid="add-podcast-page">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/podcasts"
              className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-deep mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Podcasts
            </Link>
            <h1 className="font-display text-3xl font-bold text-stone-900" data-testid="page-title">
              Add Your Podcast
            </h1>
            <p className="text-stone-600 mt-1">
              Share your podcast with the Black community
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
                <Podcast className="w-5 h-5 text-emerald-deep" />
                Podcast Information
              </h2>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="title" className="text-stone-700">
                    Podcast Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., The Black Excellence Show"
                    className="mt-1"
                    data-testid="podcast-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="host_name" className="text-stone-700">
                    Host Name(s) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="host_name"
                    name="host_name"
                    value={formData.host_name}
                    onChange={handleChange}
                    placeholder="e.g., John Smith & Jane Doe"
                    className="mt-1"
                    data-testid="host-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-stone-700">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Tell listeners what your podcast is about..."
                    rows={4}
                    className="mt-1"
                    data-testid="podcast-description-input"
                  />
                </div>

                <div>
                  <Label htmlFor="genre" className="text-stone-700">
                    Genre <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.genre}
                    onValueChange={(value) => handleSelectChange('genre', value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="genre-select">
                      <SelectValue placeholder="Select a genre" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENRES.map((genre) => (
                        <SelectItem key={genre.id} value={genre.id}>
                          {genre.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="cover_image_url" className="text-stone-700">
                    Cover Image URL
                  </Label>
                  <Input
                    id="cover_image_url"
                    name="cover_image_url"
                    value={formData.cover_image_url}
                    onChange={handleChange}
                    placeholder="https://example.com/cover.jpg"
                    className="mt-1"
                    data-testid="cover-image-input"
                  />
                </div>
              </div>
            </div>

            {/* Platform Links */}
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-2">
                Platform Links
              </h2>
              <p className="text-stone-500 text-sm mb-6">
                Add at least one platform where listeners can find your podcast
              </p>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="spotify_url" className="text-stone-700 flex items-center gap-2">
                    <Music className="w-4 h-4 text-[#1DB954]" />
                    Spotify URL
                  </Label>
                  <Input
                    id="spotify_url"
                    name="spotify_url"
                    value={formData.spotify_url}
                    onChange={handleChange}
                    placeholder="https://open.spotify.com/show/..."
                    className="mt-1"
                    data-testid="spotify-url-input"
                  />
                </div>

                <div>
                  <Label htmlFor="apple_podcasts_url" className="text-stone-700 flex items-center gap-2">
                    <Podcast className="w-4 h-4 text-purple-600" />
                    Apple Podcasts URL
                  </Label>
                  <Input
                    id="apple_podcasts_url"
                    name="apple_podcasts_url"
                    value={formData.apple_podcasts_url}
                    onChange={handleChange}
                    placeholder="https://podcasts.apple.com/..."
                    className="mt-1"
                    data-testid="apple-url-input"
                  />
                </div>

                <div>
                  <Label htmlFor="youtube_url" className="text-stone-700 flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-red-600" />
                    YouTube URL
                  </Label>
                  <Input
                    id="youtube_url"
                    name="youtube_url"
                    value={formData.youtube_url}
                    onChange={handleChange}
                    placeholder="https://youtube.com/@..."
                    className="mt-1"
                    data-testid="youtube-url-input"
                  />
                </div>

                <div>
                  <Label htmlFor="soundcloud_url" className="text-stone-700">
                    SoundCloud URL
                  </Label>
                  <Input
                    id="soundcloud_url"
                    name="soundcloud_url"
                    value={formData.soundcloud_url}
                    onChange={handleChange}
                    placeholder="https://soundcloud.com/..."
                    className="mt-1"
                    data-testid="soundcloud-url-input"
                  />
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-6">
                Social Media & Website
              </h2>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="website_url" className="text-stone-700 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-emerald-deep" />
                    Website
                  </Label>
                  <Input
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    placeholder="https://yourpodcast.com"
                    className="mt-1"
                    data-testid="website-url-input"
                  />
                </div>

                <div>
                  <Label htmlFor="instagram_url" className="text-stone-700 flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-pink-500" />
                    Instagram
                  </Label>
                  <Input
                    id="instagram_url"
                    name="instagram_url"
                    value={formData.instagram_url}
                    onChange={handleChange}
                    placeholder="https://instagram.com/..."
                    className="mt-1"
                    data-testid="instagram-url-input"
                  />
                </div>

                <div>
                  <Label htmlFor="twitter_url" className="text-stone-700 flex items-center gap-2">
                    <Twitter className="w-4 h-4 text-blue-400" />
                    Twitter/X
                  </Label>
                  <Input
                    id="twitter_url"
                    name="twitter_url"
                    value={formData.twitter_url}
                    onChange={handleChange}
                    placeholder="https://twitter.com/..."
                    className="mt-1"
                    data-testid="twitter-url-input"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/podcasts')}
                className="flex-1 h-12 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full"
                data-testid="submit-podcast-btn"
              >
                {loading ? 'Adding...' : 'Add Podcast'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AddPodcastPage;
